// New-measurement entry form. Weight respects the user's unit preference
// (kg/lb, converted to canonical kg on save via units.ts); height +
// circumferences are centimetres. Every field is optional.
//
// Model C (decided 2026-06-04): the form starts BLANK and saves ONLY the
// fields you fill — an entry records what you ACTUALLY measured, not a
// copy of last time's full panel. The last value of each field shows as a
// tappable refresh-icon chip (one tap reuses it), drawn from latestByField so
// each field's hint is its own most-recent reading. On a successful save the
// inputs clear; on failure they're kept (no silent loss) and the injected
// notify() surfaces right at the action.
//
// Lifted to @jackedlogs/ui (S3): theme + card styles come in as a prop /
// the shared settings card styles; Alert is replaced by the confirm/notify
// seams so it runs on the web console too. Logic is unchanged.
import { useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";

import {
  fromCanonicalKg,
  toCanonicalKg,
  type LatestByField,
  type Measurement,
  type NewMeasurement,
  type PatchMeasurement,
  type UnitsPreference,
} from "@jackedlogs/shared";

import { Icon } from "../../Icon/Icon";
import { TextField } from "../../primitives/TextField";
import type { UiTheme } from "../../theme";
import { makeSettingsCardStyles } from "../settingsCardStyles";
import type { ConfirmFn, NotifyFn } from "../../seams";

// cm-valued fields (height + every circumference). Weight is handled
// separately because it's unit-aware. Order = head-to-toe, then limbs
// left/right, matching how the user reads a tape down the body.
type CmKey =
  | "heightCm"
  | "neckCm"
  | "chestCm"
  | "waistCm"
  | "hipsCm"
  | "bicepsLCm"
  | "bicepsRCm"
  | "forearmsLCm"
  | "forearmsRCm"
  | "thighsLCm"
  | "thighsRCm"
  | "calvesLCm"
  | "calvesRCm";

const CM_FIELDS: Array<{ key: CmKey; defaultLabel: string }> = [
  { key: "heightCm", defaultLabel: "Height" },
  { key: "neckCm", defaultLabel: "Neck" },
  { key: "chestCm", defaultLabel: "Chest" },
  { key: "waistCm", defaultLabel: "Waist" },
  { key: "hipsCm", defaultLabel: "Hips" },
  { key: "bicepsLCm", defaultLabel: "Biceps (L)" },
  { key: "bicepsRCm", defaultLabel: "Biceps (R)" },
  { key: "forearmsLCm", defaultLabel: "Forearm (L)" },
  { key: "forearmsRCm", defaultLabel: "Forearm (R)" },
  { key: "thighsLCm", defaultLabel: "Thigh (L)" },
  { key: "thighsRCm", defaultLabel: "Thigh (R)" },
  { key: "calvesLCm", defaultLabel: "Calf (L)" },
  { key: "calvesRCm", defaultLabel: "Calf (R)" },
];

const numOrUndef = (s: string): number | undefined => {
  const v = parseFloat(s.replace(",", "."));
  return Number.isFinite(v) ? v : undefined;
};

export type MeasurementFormProps = {
  theme: UiTheme;
  lastByField: LatestByField;
  units: UnitsPreference;
  saving: boolean;
  // When set, the form edits THIS past entry (same UI, no parallel
  // editor): inputs prefill from it, refresh-icon hints hide, save → PATCH.
  editEntry?: Measurement | null;
  onSave: (payload: NewMeasurement) => Promise<boolean>;
  onUpdate: (id: string, patch: PatchMeasurement) => Promise<boolean>;
  // Editing an entry down to zero values deletes it (no empty placeholders).
  onDeleteEntry: (id: string) => Promise<boolean>;
  onCancelEdit: () => void;
  confirm: ConfirmFn;
  notify: NotifyFn;
};

export function MeasurementForm({
  theme,
  lastByField,
  units,
  saving,
  editEntry,
  onSave,
  onUpdate,
  onDeleteEntry,
  onCancelEdit,
  confirm,
  notify,
}: MeasurementFormProps) {
  const { t, i18n } = useTranslation();
  const card = useMemo(() => makeSettingsCardStyles(theme), [theme]);
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const [weight, setWeight] = useState("");
  const [cm, setCm] = useState<Record<CmKey, string>>(emptyCm);

  const isEditing = editEntry != null;
  const editId = editEntry?.id;

  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: "2-digit",
        month: "short",
        year: "2-digit",
      }),
    [i18n.language],
  );

  // Prefill from the edited entry (keyed on its id, so a background list
  // refresh doesn't clobber in-progress edits); blank again when edit ends.
  useEffect(() => {
    if (!editEntry) {
      setWeight("");
      setCm(emptyCm);
      return;
    }
    setWeight(
      editEntry.weightKg !== undefined
        ? String(fromCanonicalKg(editEntry.weightKg, units))
        : "",
    );
    setCm(() => {
      const next = { ...emptyCm };
      for (const { key } of CM_FIELDS) {
        const v = editEntry[key];
        if (typeof v === "number") next[key] = String(v);
      }
      return next;
    });
    // editEntry intentionally read fresh; deps keyed on id + units only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, units]);

  // Weight is stored canonical-kg; show + reuse it in the user's unit.
  const lastWeight =
    lastByField.weightKg !== undefined
      ? fromCanonicalKg(lastByField.weightKg.value, units)
      : undefined;

  const collectFilled = (): Omit<NewMeasurement, "takenAt" | "id"> => {
    const f: Omit<NewMeasurement, "takenAt" | "id"> = {};
    const w = numOrUndef(weight);
    if (w !== undefined) f.weightKg = toCanonicalKg(w, units);
    for (const { key } of CM_FIELDS) {
      const v = numOrUndef(cm[key]);
      if (v !== undefined) f[key] = v;
    }
    return f;
  };

  // Edit patch: the filled fields, plus an explicit null for every field
  // the entry HAD and is now empty (backspaced) → null clears it from
  // history. Fields the entry never carried stay omitted (no no-op REMOVE).
  const collectEditPatch = (entry: Measurement): PatchMeasurement => {
    const f: PatchMeasurement = { ...collectFilled() };
    if (numOrUndef(weight) === undefined && entry.weightKg !== undefined)
      f.weightKg = null;
    for (const { key } of CM_FIELDS) {
      if (numOrUndef(cm[key]) === undefined && entry[key] !== undefined)
        f[key] = null;
    }
    return f;
  };

  const hasAny =
    numOrUndef(weight) !== undefined ||
    CM_FIELDS.some(({ key }) => numOrUndef(cm[key]) !== undefined);

  // Editing an entry down to zero values: the action deletes it, so the
  // button reads "Delete" with the danger outline (not a misleading "Save").
  const deleteMode = isEditing && !hasAny;

  const submit = async () => {
    // Editing an entry down to zero values: nothing left to keep → offer to
    // delete it rather than save an empty placeholder.
    if (isEditing && editId && editEntry && !hasAny) {
      const ok = await confirm({
        title: t("mobile.body.deleteEmptyTitle", {
          defaultValue: "Remove this measurement?",
        }),
        body: t("mobile.body.deleteEmptyBody", {
          defaultValue: "All its values are cleared, so the entry will be deleted.",
        }),
        confirmLabel: t("mobile.body.deleteConfirm", { defaultValue: "Delete" }),
        cancelLabel: t("mobile.body.deleteCancel", { defaultValue: "Cancel" }),
      });
      if (ok) void onDeleteEntry(editId);
      return;
    }
    // onSave/onUpdate resolve false on a handled API error; the catch is a
    // belt-and-braces guard so an unexpected throw still keeps the typed
    // values and surfaces the notice (never a silent loss).
    let ok = false;
    try {
      ok =
        isEditing && editId && editEntry
          ? await onUpdate(editId, collectEditPatch(editEntry))
          : await onSave({ takenAt: new Date().toISOString(), ...collectFilled() });
    } catch {
      ok = false;
    }
    if (ok) {
      if (!isEditing) {
        setWeight("");
        setCm(emptyCm);
      }
    } else {
      notify(
        t("mobile.body.saveFailedTitle", { defaultValue: "Couldn't save" }),
        t("mobile.body.saveFailedBody", {
          defaultValue:
            "Your measurement wasn't saved. Your values are kept — tap Save to retry.",
        }),
      );
    }
  };

  const lastChip = (display: number | undefined, onUse: () => void) =>
    display === undefined ? (
      <View style={styles.chipSpacer} />
    ) : (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={t("mobile.body.useLastA11y", {
          defaultValue: "Use last value {{value}}",
          value: display,
        })}
        onPress={onUse}
        style={({ pressed }) => [styles.chip, pressed && styles.chipPressed]}
      >
        <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
          <Icon name="refresh" size={styles.chipText.fontSize} color={styles.chipText.color} />
          <Text style={styles.chipText}>{display}</Text>
        </View>
      </Pressable>
    );

  return (
    <View style={card.card}>
      <View style={styles.headerRow}>
        <Text style={card.cardLabel}>
          {isEditing
            ? t("mobile.body.editTitle", {
                defaultValue: "EDIT — {{date}}",
                date: dateFmt.format(new Date(editEntry.takenAt)),
              })
            : t("mobile.body.measureTitle", { defaultValue: "NEW MEASUREMENT" })}
        </Text>
        {isEditing ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("mobile.body.editCancelA11y", {
              defaultValue: "Cancel edit",
            })}
            hitSlop={8}
            onPress={onCancelEdit}
            style={({ pressed }) => [styles.cancel, pressed && styles.chipPressed]}
          >
            <Text style={styles.cancelText}>✕</Text>
          </Pressable>
        ) : null}
      </View>
      <Text style={card.cardHelp}>
        {isEditing
          ? t("mobile.body.editHelp", {
              defaultValue: "Change a value, or clear it to remove it from history.",
            })
          : t("mobile.body.measureHelpC", {
              defaultValue: "Fill only what you measured. Empty fields aren't saved.",
            })}
      </Text>

      <View style={styles.row}>
        <Text style={styles.rowLabel}>
          {t("mobile.body.weight", { defaultValue: "Weight" })}
        </Text>
        <TextField
          theme={theme}
          numeric
          align="right"
          style={styles.input}
          value={weight}
          onChangeText={setWeight}
          placeholder="—"
          accessibilityLabel={t("mobile.body.weight", { defaultValue: "Weight" })}
        />
        <Text style={styles.unit}>{units}</Text>
        {lastChip(isEditing ? undefined : lastWeight, () => setWeight(String(lastWeight)))}
      </View>

      {CM_FIELDS.map(({ key, defaultLabel }) => {
        const last = lastByField[key]?.value;
        return (
          <View key={key} style={styles.row}>
            <Text style={styles.rowLabel}>
              {t(`mobile.body.field_${key}`, { defaultValue: defaultLabel })}
            </Text>
            <TextField
              theme={theme}
              numeric
              align="right"
              style={styles.input}
              value={cm[key]}
              onChangeText={(v) => setCm((prev) => ({ ...prev, [key]: v }))}
              placeholder="—"
              accessibilityLabel={t(`mobile.body.field_${key}`, {
                defaultValue: defaultLabel,
              })}
            />
            <Text style={styles.unit}>cm</Text>
            {lastChip(isEditing ? undefined : last, () =>
              setCm((prev) => ({ ...prev, [key]: String(last) })),
            )}
          </View>
        );
      })}

      <Pressable
        accessibilityRole="button"
        disabled={saving || (!isEditing && !hasAny)}
        onPress={submit}
        style={({ pressed }) => [
          deleteMode ? card.dangerAction : card.secondaryAction,
          deleteMode && { marginTop: theme.spacing.s3 },
          pressed && (deleteMode ? card.dangerActionPressed : card.secondaryActionPressed),
          (saving || (!isEditing && !hasAny)) && card.disabled,
        ]}
      >
        {saving ? (
          <ActivityIndicator color={theme.palette.ink} />
        ) : (
          <Text style={deleteMode ? card.dangerActionText : card.secondaryActionText}>
            {deleteMode
              ? t("mobile.body.deleteConfirm", { defaultValue: "Delete" })
              : isEditing
                ? t("mobile.body.saveChanges", { defaultValue: "Save changes" })
                : t("mobile.body.measureSave", { defaultValue: "Save entered values" })}
          </Text>
        )}
      </Pressable>
    </View>
  );
}

const emptyCm: Record<CmKey, string> = {
  heightCm: "",
  neckCm: "",
  chestCm: "",
  waistCm: "",
  hipsCm: "",
  bicepsLCm: "",
  bicepsRCm: "",
  forearmsLCm: "",
  forearmsRCm: "",
  thighsLCm: "",
  thighsRCm: "",
  calvesLCm: "",
  calvesRCm: "",
};

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    headerRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    cancel: {
      minWidth: 44,
      minHeight: 44,
      alignItems: "flex-end",
      justifyContent: "center",
    },
    cancelText: { color: p.ink3, fontSize: ty.sizes.lg },
    row: { flexDirection: "row", alignItems: "center", gap: s.s3, minHeight: 44 },
    rowLabel: { flex: 1, color: p.ink, fontSize: ty.sizes.base },
    // Layout only — box internals live in the <TextField> primitive.
    input: { width: 96 },
    unit: { width: 28, color: p.ink3, fontSize: ty.sizes.sm },
    chip: {
      minWidth: 56,
      minHeight: 44,
      justifyContent: "center",
      alignItems: "flex-end",
      paddingHorizontal: s.s1,
    },
    chipPressed: { opacity: 0.5 },
    chipText: {
      color: p.brand500,
      fontSize: ty.sizes.sm,
      fontVariant: ["tabular-nums"],
    },
    chipSpacer: { minWidth: 56, paddingHorizontal: s.s1 },
  });
}
